import {
    Box,
    Card,
    CardContent,
    Grid,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import { ChangeEvent, FunctionComponent, useState } from 'react';
import { CreateFacility, Facility } from '@/models/facility';
import IconButton from '@mui/material/IconButton';
import XCircleIcon from '@heroicons/react/24/outline/XMarkIcon';
import { cloneDeep, set, unionBy } from 'lodash';

import { countryOptions } from '@/constants/countryOptions';
import { Select } from '@/components/Select/Select';
import {
    createSeoMetaDataForAllLanguages,
    SeoMetaData,
} from '@/models/seoMetaData';
import { SeoMetaDataBlock } from '@/pageContents/liftOperatorsContent/formBlocks/SeoMetaData';

type FacilityCardCreateCardProps = {
    onXClick: () => void;
    createFacility: Facility | CreateFacility;
    onChange: (value: Facility | CreateFacility) => void;
};

export const CreateFacilityCard: FunctionComponent<
    FacilityCardCreateCardProps
> = ({ onXClick, createFacility, onChange }) => {
    const [facility, setFacility] = useState(createFacility);

    const onChangePropField = (name: string, value: string) => {
        setFacility((prevFacility) => {
            const copyFacility = cloneDeep(prevFacility);

            set(copyFacility, name, value);

            return copyFacility;
        });
    };

    const onTextFieldChange =
        (name: string) => (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            onChangePropField(name, value);
        };

    const onBlur = () => {
        onChange(facility);
    };

    const onSeoMetaDataChange = (value: SeoMetaData) => {
        const copy = cloneDeep(facility);

        const seoMetaDataIndex = copy.metaData.findIndex(
            (meta) => meta.language === value.language
        );

        const nextValue = { ...value };
        if (seoMetaDataIndex > -1) {
            copy.metaData[seoMetaDataIndex] = nextValue;
        } else {
            copy.metaData = [...copy.metaData, nextValue];
        }

        onChange(copy);

        setFacility(copy);
    };

    return (
        <Box
            sx={{
                position: 'relative',
            }}
        >
            <IconButton
                onClick={onXClick}
                sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                }}
            >
                <SvgIcon fontSize='large'>
                    <XCircleIcon />
                </SvgIcon>
            </IconButton>
            <Card elevation={0} variant='outlined'>
                <CardContent>
                    <Typography
                        variant='h6'
                        sx={{
                            mb: 3,
                        }}
                    >
                        Allgemeine Daten
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid
                            sx={{
                                py: 1,
                            }}
                            md={12}
                            item
                        >
                            <TextField
                                value={facility.name}
                                onChange={onTextFieldChange('name')}
                                onBlur={onBlur}
                                label='Name:'
                                autoComplete='off'
                                fullWidth
                            />
                        </Grid>

                        <Grid
                            sx={{
                                py: 1,
                            }}
                            md={6}
                            item
                        >
                            <TextField
                                value={facility.address.street}
                                onChange={onTextFieldChange('address.street')}
                                onBlur={onBlur}
                                label='Straße:'
                                autoComplete='off'
                                fullWidth
                            />
                        </Grid>

                        <Grid
                            sx={{
                                py: 1,
                            }}
                            md={6}
                            item
                        >
                            <TextField
                                value={facility.address.zipcode}
                                onChange={onTextFieldChange('address.zipcode')}
                                onBlur={onBlur}
                                label='PLZ:'
                                autoComplete='off'
                                fullWidth
                            />
                        </Grid>

                        <Grid
                            sx={{
                                py: 1,
                            }}
                            md={6}
                            item
                        >
                            <TextField
                                value={facility.address.city}
                                onChange={onTextFieldChange('address.city')}
                                onBlur={onBlur}
                                label='Ort:'
                                autoComplete='off'
                                fullWidth
                            />
                        </Grid>

                        <Grid
                            sx={{
                                py: 1,
                            }}
                            md={6}
                            item
                        >
                            <Select
                                label='Land:'
                                value={facility.address.country}
                                onChange={onTextFieldChange('address.country')}
                                onBlur={onBlur}
                                fullWidth
                            >
                                {countryOptions.map(({ cca2, nameDE }) => (
                                    <option key={cca2} value={cca2}>
                                        {nameDE}
                                    </option>
                                ))}
                            </Select>
                        </Grid>
                    </Grid>

                    <Stack
                        sx={{
                            mt: 4,
                        }}
                        spacing={3}
                    >
                        {unionBy(
                            facility.metaData,
                            createSeoMetaDataForAllLanguages(),
                            'language'
                        ).map((meta, index) => (
                            <SeoMetaDataBlock
                                key={index}
                                seoMetaData={meta}
                                onChange={onSeoMetaDataChange}
                            />
                        ))}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};
